import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Button,
    Card,
    DatePicker,
    message,
    Upload
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getParlamentDecision,
    createParlamentDecision,
    updateParlamentDecision
} from '../../services/parlamentDecisionService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const ParlamentDecisionForm = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const navigate = useNavigate();
    const { id: guid } = useParams();

    useEffect(() => {
        if (guid) {
            fetchParlamentDecision();
        }
    }, [guid]);

    const fetchParlamentDecision = async () => {
        try {
            setLoading(true);
            const data = await getParlamentDecision(guid);
            form.setFieldsValue({
                ...data,
                date: dayjs(data.date).local()
            });
            if (data.fileUrl) {
                setFileList([
                    {
                        uid: '-1',
                        name: 'Mevcut Dosya',
                        status: 'done',
                        url: data.fileUrl
                    }
                ]);
            }
        } catch (error) {
            message.error('Meclis kararı yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            console.log('Seçilen tarih (values.date):', values.date);
            console.log('Tarih formatı:', values.date.format());
            console.log('UTC formatı:', values.date.utc().format());
            console.log('Yerel saat:', values.date.local().format());
            
            const submitData = {
                decisionsNo: values.decisionsNo || '',
                header: values.header,
                detail: values.detail,
                Date: values.date.utc().format('YYYY-MM-DD'),
                file: fileList[0]?.originFileObj
            };
            
            console.log('API\'ye gönderilen veri:', submitData);

            if (guid) {
                await updateParlamentDecision(guid, submitData);
                message.success('Meclis kararı başarıyla güncellendi.');
            } else {
                await createParlamentDecision(submitData);
                message.success('Meclis kararı başarıyla oluşturuldu.');
            }
            navigate('/parlament-decisions');
        } catch (error) {
            console.error('Form gönderme hatası:', error);
            message.error('Bir hata oluştu: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        beforeUpload: (file) => {
            // Allow zip and rar files
            const fileExt = file.name.toLowerCase();
            const isAllowed = fileExt.endsWith('.zip') || 
                            fileExt.endsWith('.rar') ||
                            file.type === 'application/zip' || 
                            file.type === 'application/x-zip-compressed' ||
                            file.type === 'application/x-rar-compressed';

            if (!isAllowed) {
                message.error('Sadece ZIP veya RAR dosyaları yüklenebilir!');
                return Upload.LIST_IGNORE;
            }
            setFileList([file]);
            return false;
        },
        fileList,
        onChange: ({ fileList }) => setFileList(fileList),
        accept: '.zip,.rar'
    };

    return (
        <Card title={guid ? 'Meclis Kararı Düzenle' : 'Yeni Meclis Kararı'}>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    date: dayjs().startOf('day').utc()
                }}
                onValuesChange={(changedValues) => {
                    if (changedValues.date) {
                        console.log('DatePicker değişti:', changedValues.date.format());
                    }
                }}
            >
                <Form.Item
                    name="date"
                    label="Tarih"
                    rules={[{ required: true, message: 'Lütfen tarih seçiniz' }]}
                >
                    <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD.MM.YYYY"
                    showTime={false}
                />
                </Form.Item>

                <Form.Item
                    name="decisionsNo"
                    label="Karar No"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="header"
                    label="Başlık"
                    rules={[{ required: true, message: 'Lütfen başlık giriniz' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="detail"
                    label="Detay"
                    rules={[{ required: true, message: 'Lütfen detay giriniz' }]}
                    getValueFromEvent={(value) => value}
                    normalize={(value) => value || ''}
                >
                    <ReactQuill
                        theme="snow"
                        modules={{
                            toolbar: [
                                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                [{ 'font': [] }],
                                [{ 'size': ['small', false, 'large', 'huge'] }],
                                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                [{ 'color': [] }, { 'background': [] }],
                                [{ 'script': 'sub'}, { 'script': 'super' }],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                                [{ 'align': [] }],
                                ['link', 'image', 'video'],
                                ['clean'],
                                ['code-block']
                            ]
                        }}
                        style={{ height: '400px', marginBottom: '50px' }}
                    />
                </Form.Item>

                <Form.Item
                    name="fileUrl"
                    label="Dosya"
                >
                    <Upload {...uploadProps} maxCount={1}>
                        <Button icon={<UploadOutlined />}>Dosya Seç</Button>
                    </Upload>
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {guid ? 'Güncelle' : 'Kaydet'}
                    </Button>
                    <Button 
                        style={{ marginLeft: 8 }}
                        onClick={() => navigate('/parlament-decisions')}
                    >
                        İptal
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default ParlamentDecisionForm;
